import type { IsoDateTime } from './common';
import type { OrderId } from './ids';
import type { MarketplaceChannel } from './marketplace';
import type { OrderStatus } from './orders';
import { normalizeScan, scanSpellings } from './scan';

// Acil iş (floor_actions, migration 0069): what the floor must do now about one order because something
// changed after it started on it. The marketplace moved the parcel to another carrier after its label was
// printed (relabel), or the order was cancelled (or held because its marketplace no longer knows it, 0072)
// with its units out of their bins (put_back) or only its label printed (discard_label), or a line of a
// closed parcel was cancelled (repack). Every floor screen shows the open ones in a red bar under the top
// bar until they are done; the team's chat hears of each new one. Order numbers, SKUs, carriers and
// tracking numbers only: no buyer's data.

export const URGENT_ACTION_KINDS = ['relabel', 'put_back', 'discard_label', 'repack'] as const;
export type UrgentActionKind = (typeof URGENT_ACTION_KINDS)[number];

/** How an acil iş was closed: its new label scanned, a button pressed, or by itself (the work it waited for was done). */
export const URGENT_DONE_HOWS = ['scan', 'button', 'auto'] as const;
export type UrgentDoneHow = (typeof URGENT_DONE_HOWS)[number];

export const URGENT_KIND_LABELS: Readonly<Record<UrgentActionKind, string>> = {
  relabel: 'Kargo değişti',
  put_back: 'Ürünleri yerine götür',
  discard_label: 'Etiketi at',
  repack: 'Yeniden paketle',
};

export const URGENT_DONE_HOW_LABELS: Readonly<Record<UrgentDoneHow, string>> = {
  scan: 'yeni etiket okutuldu',
  button: 'elle onaylandı',
  auto: 'iş yapılınca kendiliğinden',
};

/** Units of a product: "001-35 ×2". */
export interface UrgentUnit {
  readonly sku: string;
  readonly qty: number;
}

/** What an action is about, by kind (floor_actions.details). */
export interface UrgentDetails {
  /** relabel: the carriers (as their names read) and the tracking numbers before and after */
  readonly from?: string | null;
  readonly to?: string | null;
  readonly oldTracking?: string | null;
  readonly newTracking?: string | null;
  /** relabel: the desk whose printer prints the new label (the one that printed the old one) */
  readonly station?: string | null;
  /** relabel: when the new label went to that desk's printer; null while it is being drawn */
  readonly printedAt?: IsoDateTime | null;
  /** put_back, repack: the units */
  readonly units?: readonly UrgentUnit[];
  /** the parcel was closed (packed) */
  readonly packed?: boolean;
  /** put_back, discard_label: the whole order was cancelled (else a line of it) */
  readonly cancelled?: boolean;
  /**
   * put_back, discard_label: the order waits on hold, its marketplace no longer knows it (0072): nothing of it goes
   * while the supervisor decides
   */
  readonly held?: boolean;
  /** put_back of that hold: the hold was lifted (Yeniden dene) before its units were put away; they still go back (0073) */
  readonly lifted?: boolean;
  /** put_back: a label of the order came out (it is taken off and thrown away) */
  readonly label?: boolean;
  /** discard_label: the label's tracking number */
  readonly tracking?: string | null;
}

/** One acil iş, as the red bar and its panel show it (GET /api/wms/floor-actions). */
export interface UrgentAction {
  readonly id: string;
  readonly kind: UrgentActionKind;
  readonly orderId: OrderId;
  readonly orderNumber: string;
  /** the marketplace the order came from */
  readonly channel: MarketplaceChannel;
  readonly tenantCode: string;
  /** where the order stands now (a relabel of a packed parcel waits for its new label's scan) */
  readonly orderStatus: OrderStatus;
  /** the order's tracking number now: the new label's (a relabel is closed by scanning it) */
  readonly trackingNumber: string | null;
  /** numbers of labels printed before a carrier change: refused when scanned */
  readonly supersededTracking: readonly string[];
  /** the bar's words after the order number: "kargo değişti, etiketi değiştirin" */
  readonly title: string;
  /** what the floor reads and does, in full */
  readonly instruction: string;
  readonly details: UrgentDetails;
  readonly createdAt: IsoDateTime;
  readonly updatedAt: IsoDateTime;
  readonly doneAt: IsoDateTime | null;
  /** who closed it (a person's name); null when nobody did (the order was cancelled or left with the carrier) */
  readonly doneBy: string | null;
  readonly doneHow: UrgentDoneHow | null;
}

/** POST /api/wms/floor-actions/:id/done: the label scanned on the panel (relabel), or nothing (a button). */
export interface UrgentDoneRequest {
  readonly code?: string;
}

/** "001-35 ×2, 001-50 ×1" */
export const urgentUnitsText = (units: readonly UrgentUnit[]): string => units.map((u) => `${u.sku} ×${u.qty}`).join(', ');

/** The last four characters of a tracking number, as the floor finds it on a label: "…5941". */
export const lastFour = (tracking: string | null | undefined): string | null => (tracking ? `…${tracking.slice(-4)}` : null);

/** What a relabel says, from the carrier change and where the new label prints. */
export interface RelabelInput {
  readonly orderNumber: string;
  /** carrier names: "Trendyol Express", "Aras Kargo" */
  readonly from: string;
  readonly to: string;
  readonly oldTracking: string | null;
  readonly newTracking: string | null;
  /** the desk whose printer prints the new label; null: no desk set up */
  readonly station: string | null;
  /** the parcel is packed (the label on it is replaced) or still on the floor (the old one is not stuck on) */
  readonly packed: boolean;
  /** the new label went to the desk's printer (false while the marketplace draws it) */
  readonly printed: boolean;
}

/**
 * The relabel's bar words and instruction: "KARGO DEĞİŞTİ: 11760934092 · Trendyol Express → Aras Kargo.
 * Eski etiket (…5941 son 4 hane) geçersiz. Yeni etiket PK-01 yazıcısından çıktı: eskisinin üstüne
 * yapıştırın ve yeni etiketi okutun." A parcel still on the floor goes to a desk, which prints the new
 * label as it packs.
 */
export function relabelText(input: RelabelInput): { readonly title: string; readonly instruction: string } {
  const sameCarrier = input.from === input.to;
  const head = sameCarrier
    ? `TAKİP NO DEĞİŞTİ: ${input.orderNumber} · ${input.to}`
    : `KARGO DEĞİŞTİ: ${input.orderNumber} · ${input.from} → ${input.to}`;
  // the same number under another carrier (Hepsiburada keeps the package's barcode): the carrier's name on it is wrong
  const old =
    input.oldTracking && input.oldTracking !== input.newTracking
      ? `Eski etiket (${lastFour(input.oldTracking)} son 4 hane) geçersiz`
      : `Eski etiket (${input.from} yazan) geçersiz`;
  const where = input.station ? `${input.station} yazıcısından` : 'paketleme masasının yazıcısından';
  const fresh = input.printed ? `Yeni etiket ${where} çıktı` : `Yeni etiket ${where} çıkacak`;
  return {
    title: sameCarrier ? 'takip no değişti, etiketi değiştirin' : 'kargo değişti, etiketi değiştirin',
    instruction: input.packed
      ? `${head}. ${old}. ${fresh}: eskisinin üstüne yapıştırın ve yeni etiketi okutun.`
      : `${head}. ${old}: ürüne yapıştırmayın, yapıştırdıysanız sökün. ${fresh}; sipariş paketleme masasında yeni etiketle paketlenir.`,
  };
}

/** The walk, on an order whose label taken along became void (pick_route_orders.label_void_at). */
export const VOID_LABEL_TEXT = 'Kargo değişti: bu siparişin elinizdeki etiketini YAPIŞTIRMAYIN (yapıştırdıysanız sökün). Yeni etiket masada.';

/** A label printed before the carrier change, scanned where only the new one counts (the dock, the acil iş panel). */
export function supersededLabelText(orderNumber: string, carrier: string, trackingNumber: string | null): string {
  return `${orderNumber}: bu etiket geçersiz, kargo firması ${carrier} olarak değişti. Yeni etiketi (takip no ${trackingNumber ?? '—'}) basıp eskisinin üstüne yapıştırın.`;
}

/** The dock (or the panel) read something else than the new label of a parcel whose carrier changed. */
export function relabelOpenText(orderNumber: string, trackingNumber: string | null): string {
  return `KARGO DEĞİŞTİ: ${orderNumber} paketinin üstündeki eski etiketi sökün, yeni etiketi${trackingNumber ? ` (takip no ${trackingNumber})` : ''} yapıştırıp okutun.`;
}

/**
 * Which label a code read for a relabel is: the new one ('new': its tracking number, or the order number
 * when the parcel has none), one printed before the carrier change ('old'), or neither. A reader on
 * another keyboard layout is read as it meant the code (scanSpellings). A carrier switch that kept the
 * barcode (Hepsiburada) cannot be told apart: its label reads as the new one.
 */
export function relabelScan(
  code: string,
  order: { readonly orderNumber: string; readonly trackingNumber: string | null; readonly supersededTracking: readonly string[] },
): 'new' | 'old' | 'other' {
  const read = scanSpellings(code);
  const is = (value: string | null) => value !== null && read.includes(normalizeScan(value));
  if (order.trackingNumber ? is(order.trackingNumber) : is(order.orderNumber)) return 'new';
  if (order.supersededTracking.some(is)) return 'old';
  return 'other';
}

/** Said at the end of a bildirim whose work is an acil iş: where the floor finds and closes it. */
export const URGENT_POINTER = 'Acil iş: ekranın üstündeki kırmızı ACİL çubuğuna dokunun.';

/** The red bar: "⚠ ACİL: 11760934092 — kargo değişti, etiketi değiştirin ve 2 iş daha"; null while nothing is open. */
export function urgentBarText(actions: readonly Pick<UrgentAction, 'orderNumber' | 'title'>[]): string | null {
  const [first] = actions;
  if (!first) return null;
  return `⚠ ACİL: ${first.orderNumber} — ${first.title}${actions.length > 1 ? ` ve ${actions.length - 1} iş daha` : ''}`;
}

/** The first line of every acil iş message in the team's chat. */
export const URGENT_ALERT_HEADER = 'Pronto Depo · Acil iş';

/**
 * The team's chat (Telegram / webhook): one message per new action, with the order number, the seller's
 * code, carriers and SKUs; never a buyer's name, address or phone.
 */
export function urgentAlertText(action: Pick<UrgentAction, 'kind' | 'orderNumber' | 'tenantCode' | 'details'>): string {
  const d = action.details;
  const who = `${action.orderNumber} (${action.tenantCode})`;
  const units = urgentUnitsText(d.units ?? []);
  const line = (() => {
    switch (action.kind) {
      case 'relabel': {
        const where = d.station ? `${d.station} yazıcısından` : 'paketleme masasının yazıcısından';
        const fresh = d.printedAt ? `yeni etiket ${where} çıktı` : `yeni etiket ${where} çıkacak`;
        const change = d.from === d.to ? `${d.to ?? 'kargo'} takip numarası değişti` : `${d.from ?? '—'} → ${d.to ?? '—'}`;
        return d.packed
          ? `⚠️ Kargo değişti — ${who}: ${change}. Basılan etiket geçersiz; ${fresh}, paketin üstündekiyle değiştirin.`
          : `⚠️ Kargo değişti — ${who}: ${change}. Basılan etiket geçersiz, ürüne yapıştırmayın; ${fresh}, sipariş masada yeni etiketle paketlenir.`;
      }
      case 'put_back':
        if (d.held) {
          return d.packed
            ? `⏸️ Beklemede — ${who}: pazaryerinde bulunamadı, gönderilmeyecek. Paketi kuryeye vermeyin; açıp ürünleri yerine götürün (${units}).`
            : `⏸️ Beklemede — ${who}: pazaryerinde bulunamadı, gönderilmeyecek. Toplanan ürünleri yerine götürün (${units}).`;
        }
        return d.cancelled === false
          ? `⛔ Ürün iptal — ${who}: ${units} iptal edildi, ürünü yerine götürün.`
          : d.packed
            ? `⛔ İptal — ${who}: paketi kuryeye vermeyin; açıp ürünleri yerine götürün (${units}).`
            : `⛔ İptal — ${who}: toplanan ürünleri yerine götürün (${units}).`;
      case 'discard_label':
        return d.held
          ? `⏸️ Beklemede — ${who}: pazaryerinde bulunamadı, gönderilmeyecek. Basılan kargo etiketini atın; ürün toplanmadı.`
          : `⛔ İptal — ${who}: basılan kargo etiketini atın; ürün toplanmadı.`;
      case 'repack':
        return `📦 Ürün iptal — ${who}: paketi kuryeye vermeyin; açın, ${units} çıkarın, yeniden paketleyin.`;
    }
  })();
  return `${URGENT_ALERT_HEADER}\n${line}`;
}
